import {
  DEFAULT_STOREFRONT_TEMPLATE_ID,
  PAGE_MARGIN_PX,
  type PageAudience,
  type PageTemplateChoices,
  type PickableStorefrontPage,
  STOREFRONT_PAGE_TEMPLATE_INFO,
  type StorefrontFeatureKey,
  type StorefrontTheme,
} from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button, buttonVariants } from "@loot-os/ui/components/button";
import { Input } from "@loot-os/ui/components/input";
import { toast } from "@loot-os/ui/components/sonner";
import { cn } from "@loot-os/ui/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { Fragment, type ReactNode, useMemo, useState } from "react";

import { PageLayoutSketch } from "../components/PageLayoutSketch";
import { ChoiceField } from "../components/StorefrontFields";
import { StorefrontSection } from "../components/StorefrontSection";
import { ThemePreviewPanel } from "../components/ThemePreviewPanel";
import { UnsavedChangesDialog } from "../components/UnsavedChangesDialog";
import {
  currentLayout,
  type PageTemplateDrafts,
  PICKABLE_PAGES,
  previewPicks,
  sharedLayout,
  sharedLayouts,
  templateLayout,
  toPick,
} from "../lib/pageTemplatePicks";
import { useStorefrontConfig } from "../lib/storefront";
import {
  allPagesEntries,
  groupPagesEntries,
  layoutSections,
  PAGE_ENTRIES,
  type StorefrontPagesEntry,
  type StorefrontPagesEntryId,
  sectionsLeftOut,
} from "../lib/storefrontPages";
import {
  choiceValue,
  SITE_FRAME_CHOICES,
  type ThemeChoice,
  toSavedTheme,
  withChoice,
} from "../lib/storefrontTheme";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";
import { useThemePreviewInputs } from "../lib/useThemePreviewInputs";
import { useUnsavedChangesGuard } from "../lib/useUnsavedChangesGuard";

/** The site frame's fields the page edits, by their key in `SITE_FRAME_CHOICES`. */
type FrameKey = keyof typeof SITE_FRAME_CHOICES;
type FrameDraft = Partial<Record<FrameKey, string | number>>;

/** The header and footer fields, in the order Header and footer shows them. */
const HEADER_FOOTER_KEYS = [
  "header",
  "chrome",
  "navLinks",
  "breadcrumb",
  "footer",
  "pageMargin",
] as const satisfies FrameKey[];

/** The chat fields, which the Chat entry shows: an edit to one marks Chat, not Header and footer. */
const CHAT_FRAME_KEYS: readonly FrameKey[] = ["chat", "chatRows"];

/**
 * Storefront → Pages: how the store is put together, beside a live preview. A list down the side
 * names what every page shares (the header and footer, the chat panel) and every page, a
 * feature's pages as one group (Battles, Forge), with the pages whose feature is off under Not
 * shown, so a store can set one up before turning it on. A page with more than one layout offers
 * them as outlines, with the sections the chosen one shows; a group offers the layouts all its
 * pages have and sets every one of them, each page still free to pick its own after.
 * The page's changes (layouts and the site frame) are one draft, saved together from the bar at
 * the bottom. The site frame is stored in the theme (`theme.layout`), written back whole from the
 * stored theme with only those fields changed, so a draft on the Theme tab is never sent from here.
 * Leaving with unsaved changes asks first (`useUnsavedChangesGuard`).
 */
export function StorefrontPagesPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const { can } = useTenantRole();
  const [openEntry, setOpenEntry] = useState<StorefrontPagesEntryId>("home");
  // Who Home is shown for, in the preview and the section list alike.
  const [audience, setAudience] = useState<PageAudience>("signedIn");
  const [picks, setPicks] = useState<PageTemplateDrafts>({});
  const [frame, setFrame] = useState<FrameDraft>({});
  const [leftOut, setLeftOut] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const discard = () => {
    setPicks({});
    setFrame({});
    setLeftOut(null);
  };
  const { query, update, queryOptions } = useStorefrontConfig();
  const previewInputs = useThemePreviewInputs();
  const setPageTemplate = useMutation(trpc.storefront.setPageTemplate.mutationOptions());

  // Above the narrowing below, as hooks must be. Memoized so the preview posts only on a change.
  const stored = query.data?.config;
  const savedPicks = stored?.pageTemplates ?? NO_PICKS;
  const framedTheme = useMemo(
    () => toSavedTheme(withFrame(stored?.theme ?? {}, frame)),
    [stored?.theme, frame],
  );
  const pageTemplates = useMemo(() => previewPicks(savedPicks, picks), [savedPicks, picks]);

  // Counted before the narrowing below, so the guard (a hook) knows whether the page is dirty.
  const base = stored?.storefrontTemplate ?? DEFAULT_STOREFRONT_TEMPLATE_ID;
  const changedPages = PICKABLE_PAGES.filter(
    (page) => picks[page] !== undefined && picks[page] !== currentLayout(base, savedPicks, page),
  );
  const changedFrame = (Object.keys(frame) as FrameKey[]).filter(
    (key) => frame[key] !== choiceValue(stored?.theme ?? {}, SITE_FRAME_CHOICES[key]),
  );
  const changes = changedPages.length + changedFrame.length;

  /** Saves every changed page and the site frame; rejects when one fails, keeping the draft. */
  const persist = async () => {
    setSaving(true);
    try {
      for (const page of changedPages) {
        const id = picks[page] as string;
        await setPageTemplate.mutateAsync({ page, template: toPick(base, page, id) });
      }
      if (changedFrame.length > 0) {
        // Its own toast says the storefront saved.
        await update.mutateAsync({ theme: framedTheme });
      } else {
        toast.success("Pages saved.");
      }
      await queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
      discard();
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    // The mutation's toast says why a save failed; the draft stays for another try.
    persist().catch(() => {});
  };

  const guard = useUnsavedChangesGuard({
    dirty: changes > 0,
    // Not while a save is in flight: a second one could land before the first.
    onSave: can("editor") && !saving ? persist : undefined,
    onDiscard: discard,
  });

  // Narrowing only: the layout renders a section once the same query has data.
  if (!query.data) return null;

  const { config, environment } = query.data;
  const canEdit = can("editor");
  // Resolved, so a feature whose tenant module is off reads as off.
  const hasFeature = (key: StorefrontFeatureKey) => previewInputs.store.features[key];
  const groups = groupPagesEntries(hasFeature);
  const entry =
    allPagesEntries().find((candidate) => candidate.id === openEntry) ??
    (PAGE_ENTRIES[0] as StorefrontPagesEntry);
  const hidden = entry.feature !== undefined && !hasFeature(entry.feature);

  const pick = (page: PickableStorefrontPage, id: string) => {
    const current = picks[page] ?? currentLayout(base, savedPicks, page);
    const left = sectionsLeftOut(page, current, id, audience);
    const info: Record<string, { label: string }> = STOREFRONT_PAGE_TEMPLATE_INFO[page];
    setLeftOut(left.length > 0 ? `${info[id]?.label ?? id} leaves out ${listWords(left)}.` : null);
    setPicks((current) => ({ ...current, [page]: id }));
  };

  const layoutOf = (page: PickableStorefrontPage) =>
    picks[page] ?? currentLayout(base, savedPicks, page);

  /** Sets every page of a group that offers `id` to it. */
  const pickForGroup = (pages: readonly PickableStorefrontPage[], id: string) => {
    setLeftOut(null);
    setPicks((current) => ({
      ...current,
      ...Object.fromEntries(pages.map((page) => [page, id])),
    }));
  };

  const isEdited = (item: StorefrontPagesEntry): boolean =>
    (item.id === "headerFooter" && changedFrame.some((key) => !CHAT_FRAME_KEYS.includes(key))) ||
    (item.id === "chat" && changedFrame.some((key) => CHAT_FRAME_KEYS.includes(key))) ||
    changedPages.includes(item.id as PickableStorefrontPage) ||
    (item.children ?? []).some(isEdited);

  const navRow = (item: StorefrontPagesEntry, nested = false) => {
    const off = item.feature !== undefined && !hasFeature(item.feature);
    return (
      <button
        key={item.id}
        type="button"
        aria-current={item.id === entry.id ? "page" : undefined}
        onClick={() => {
          setOpenEntry(item.id);
          setLeftOut(null);
        }}
        className={cn(
          "flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent",
          nested && "ml-3 border-l pl-3 rounded-l-none",
          item.id === entry.id && "bg-accent font-medium",
          off && "text-muted-foreground",
        )}
      >
        <span className="flex-1">{item.label}</span>
        {off && !nested && (
          <Badge variant="outline" className="h-4 px-1.5 text-[0.65rem]">
            Off
          </Badge>
        )}
        {isEdited(item) && (
          <span className="size-1.5 rounded-full bg-primary" title="Unsaved" aria-hidden />
        )}
      </button>
    );
  };

  return (
    <StorefrontSection
      title="Pages"
      description="How your store is put together: what every page shares, and how each page is laid out. Every layout shows the same content; only the arrangement differs."
      environment={environment}
    >
      <UnsavedChangesDialog guard={guard} />
      <div className="grid items-start gap-6 lg:grid-cols-[11rem_minmax(0,1fr)] 2xl:grid-cols-[11rem_minmax(0,1fr)_minmax(0,1.1fr)]">
        <nav aria-label="Pages" className="grid gap-4 max-lg:grid-cols-2 max-sm:grid-cols-1">
          {groups.map((group) => (
            <div key={group.title} className="grid content-start gap-0.5">
              <h2 className="px-2 pb-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                {group.title}
              </h2>
              {group.entries.map((item) => (
                <Fragment key={item.id}>
                  {navRow(item)}
                  {item.children?.map((child) => navRow(child, true))}
                </Fragment>
              ))}
            </div>
          ))}
        </nav>

        <div className="grid min-w-0 content-start gap-4">
          {hidden && entry.featurePath && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed px-4 py-3 text-sm">
              <span className="min-w-0 flex-1 text-muted-foreground">
                {entry.label} is off, so shoppers don't see it. Set it up here now; it goes live as
                you left it when you turn it on.
              </span>
              <Link
                to={entry.featurePath}
                params={{ tenantId }}
                className={buttonVariants({ variant: "outline", size: "sm" })}
              >
                {entry.label} settings
              </Link>
            </div>
          )}

          {entry.id === "headerFooter" && (
            <Panel title="Header and footer" description="Shared by every page.">
              {HEADER_FOOTER_KEYS.map((key) => (
                <FrameChoice
                  key={key}
                  frameKey={key}
                  theme={config.theme}
                  draft={frame}
                  canEdit={canEdit}
                  onChange={setFrame}
                />
              ))}
            </Panel>
          )}

          {entry.id === "chat" && (
            <Panel title="Chat" description="The chat panel shoppers open from any page.">
              <FrameChoice
                frameKey="chat"
                theme={config.theme}
                draft={frame}
                canEdit={canEdit}
                onChange={setFrame}
              />
              <p className="text-xs text-muted-foreground">
                A drawer opens over the page from the header. Docked, chat sits beside the page,
                open until a shopper hides it, and the page slides over to keep its width.
              </p>
              <FrameChoice
                frameKey="chatRows"
                theme={config.theme}
                draft={frame}
                canEdit={canEdit}
                onChange={setFrame}
              />
              <p className="text-xs text-muted-foreground">
                Cards give each message a block of its own; lines fit more on screen.
              </p>
            </Panel>
          )}

          {isPickable(entry.id) && (
            <PagePanel
              page={entry.id}
              label={entry.label}
              chosen={picks[entry.id] ?? currentLayout(base, savedPicks, entry.id)}
              fromTemplate={templateLayout(base, entry.id)}
              canEdit={canEdit}
              note={leftOut}
              hasFeature={hasFeature}
              audience={audience}
              onPick={(id) => pick(entry.id as PickableStorefrontPage, id)}
            />
          )}

          {entry.children && (
            <GroupPanel
              group={entry}
              layoutOf={(page) => layoutOf(page)}
              fromTemplate={(page) => templateLayout(base, page)}
              canEdit={canEdit}
              onPick={pickForGroup}
              onOpen={(id) => {
                setOpenEntry(id);
                setLeftOut(null);
              }}
            />
          )}

          {entry.id !== "headerFooter" &&
            entry.id !== "chat" &&
            !entry.children &&
            !isPickable(entry.id) && (
              <Panel title={entry.label}>
                <p className="text-sm text-muted-foreground">
                  One layout so far. More arrive here as they are designed.
                </p>
              </Panel>
            )}
        </div>

        <ThemePreviewPanel
          {...previewInputs}
          theme={framedTheme}
          template={base}
          pageTemplates={pageTemplates}
          page={entry.preview}
          audience={audience}
          onAudienceChange={setAudience}
          className="h-128 lg:col-span-2 2xl:sticky 2xl:top-4 2xl:col-span-1 2xl:h-[calc(100vh-2rem)]"
        />
      </div>

      {changes > 0 && (
        <div className="sticky bottom-4 z-10 mt-6 animate-in fade-in-0 slide-in-from-bottom-4 duration-200">
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-primary/40 bg-popover px-4 py-3 shadow-2xl ring-1 ring-black/20 sm:px-5">
            <span className="relative flex size-2.5 shrink-0" aria-hidden>
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-60 motion-reduce:hidden" />
              <span className="relative inline-flex size-2.5 rounded-full bg-primary" />
            </span>
            <div className="grid min-w-0 gap-0.5">
              <span className="text-sm font-medium">
                {changes === 1 ? "1 unsaved change" : `${changes} unsaved changes`}
              </span>
              <span className="text-xs text-muted-foreground">
                Nothing reaches the storefront until you save.
              </span>
            </div>
            <div className="ml-auto flex gap-2">
              <Button type="button" variant="outline" disabled={saving} onClick={discard}>
                Discard
              </Button>
              <Button type="button" disabled={saving || !canEdit} onClick={save}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </StorefrontSection>
  );
}

/** A card holding one entry's settings. */
function Panel({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="grid gap-5 rounded-xl border bg-card p-4 sm:p-5">
      <div className="grid gap-1">
        <h2 className="text-base font-semibold">{title}</h2>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {children}
    </section>
  );
}

/**
 * A group of pages (Battles, Forge): the layouts every page in it offers, picking one sets them
 * all, and Mixed while they differ. Each page below opens to give it a layout of its own.
 */
function GroupPanel({
  group,
  layoutOf,
  fromTemplate,
  canEdit,
  onPick,
  onOpen,
}: {
  group: StorefrontPagesEntry;
  layoutOf: (page: PickableStorefrontPage) => string;
  fromTemplate: (page: PickableStorefrontPage) => string;
  canEdit: boolean;
  onPick: (pages: readonly PickableStorefrontPage[], id: string) => void;
  onOpen: (id: StorefrontPagesEntryId) => void;
}) {
  const children = group.children ?? [];
  const pages = children.map((child) => child.id).filter(isPickable);
  // Every page's own layouts, picker or not: a page's preview is the page itself.
  const shared = sharedLayouts(children.map((child) => child.preview));
  const layouts = pages.length === children.length ? shared : [];
  const [first] = pages;
  const chosen = sharedLayout(pages.map(layoutOf));
  const labelOf = (page: PickableStorefrontPage, id: string) =>
    (STOREFRONT_PAGE_TEMPLATE_INFO[page] as Record<string, { label: string }>)[id]?.label ?? id;
  return (
    <Panel
      title={group.label}
      description={
        layouts.length > 1
          ? `Sets every ${group.label} page at once. Open a page below to give it a layout of its own.`
          : shared.length === 1
            ? `The ${group.label} pages have one layout so far. More arrive here as they are designed.`
            : `The ${group.label} pages share no layout yet, so each picks its own. Open one below.`
      }
    >
      {layouts.length > 1 && first && (
        <>
          <fieldset
            className="grid grid-cols-2 gap-3 sm:grid-cols-3"
            disabled={!canEdit}
            aria-label={`${group.label} layout`}
          >
            {layouts.map((id) => (
              <label
                key={id}
                className={cn(
                  "flex cursor-pointer flex-col gap-2 rounded-xl border bg-card p-2 transition-colors has-focus-visible:ring-[3px] has-focus-visible:ring-ring/50 has-disabled:cursor-default",
                  chosen === id
                    ? "border-primary text-primary"
                    : "border-border text-muted-foreground hover:border-primary/50",
                )}
              >
                <input
                  type="radio"
                  name={`group-layout-${group.id}`}
                  value={id}
                  checked={chosen === id}
                  onChange={() => onPick(pages, id)}
                  className="sr-only"
                />
                <PageLayoutSketch page={first} layout={id} />
                <span className="flex flex-wrap items-center gap-1.5 px-1 text-sm font-medium text-foreground">
                  {labelOf(first, id)}
                  {pages.every((page) => fromTemplate(page) === id) && (
                    <span className="text-xs font-normal text-muted-foreground">Default</span>
                  )}
                </span>
              </label>
            ))}
          </fieldset>
          {chosen === null && (
            <p className="text-xs text-muted-foreground">
              Mixed: the pages below use different layouts. Pick one to set them all.
            </p>
          )}
        </>
      )}
      <ul className="-mx-4 border-t sm:-mx-5">
        {children.map((child) => (
          <li key={child.id} className="border-b last:border-b-0">
            <button
              type="button"
              onClick={() => onOpen(child.id)}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-accent sm:px-5"
            >
              <span className="flex-1 font-medium">{child.label}</span>
              <span className="text-xs text-muted-foreground">
                {isPickable(child.id) ? labelOf(child.id, layoutOf(child.id)) : "One layout"}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/** A page with layouts to choose: each as an outline, and the sections the chosen one shows. */
function PagePanel({
  page,
  label,
  chosen,
  fromTemplate,
  canEdit,
  note,
  hasFeature,
  audience,
  onPick,
}: {
  page: PickableStorefrontPage;
  label: string;
  chosen: string;
  fromTemplate: string;
  canEdit: boolean;
  note: string | null;
  hasFeature: (key: StorefrontFeatureKey) => boolean;
  /** Who the section list is for: the preview's Signed in or Signed out. */
  audience: PageAudience;
  onPick: (id: string) => void;
}) {
  const layouts: [string, { label: string; description: string }][] = Object.entries(
    STOREFRONT_PAGE_TEMPLATE_INFO[page],
  );
  const chosenInfo = layouts.find(([id]) => id === chosen)?.[1];
  return (
    <>
      <Panel title={label} description={chosenInfo?.description}>
        <fieldset
          className="grid grid-cols-2 gap-3 sm:grid-cols-3"
          disabled={!canEdit}
          aria-label={`${label} layout`}
        >
          {layouts.map(([id, info]) => (
            <label
              key={id}
              className={cn(
                "flex cursor-pointer flex-col gap-2 rounded-xl border bg-card p-2 transition-colors has-focus-visible:ring-[3px] has-focus-visible:ring-ring/50 has-disabled:cursor-default",
                chosen === id
                  ? "border-primary text-primary"
                  : "border-border text-muted-foreground hover:border-primary/50",
              )}
            >
              <input
                type="radio"
                name={`page-layout-${page}`}
                value={id}
                checked={chosen === id}
                onChange={() => onPick(id)}
                className="sr-only"
              />
              <PageLayoutSketch page={page} layout={id} />
              <span className="flex flex-wrap items-center gap-1.5 px-1 text-sm font-medium text-foreground">
                {info.label}
                {id === fromTemplate && (
                  <span className="text-xs font-normal text-muted-foreground">Default</span>
                )}
              </span>
            </label>
          ))}
        </fieldset>
        {note && <p className="text-xs text-muted-foreground">{note}</p>}
      </Panel>
      <section className="overflow-hidden rounded-xl border bg-card">
        <h3 className="px-4 pt-3 pb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase sm:px-5">
          Sections, top to bottom
          {page === "home" && (
            <span className="font-normal normal-case tracking-normal">
              {audience === "signedIn" ? " · for a signed-in shopper" : " · for a visitor"}
            </span>
          )}
        </h3>
        <ul>
          {layoutSections(page, chosen, audience).map((section) => {
            const off = section.feature !== undefined && !hasFeature(section.feature);
            return (
              <li
                key={section.id}
                className={cn(
                  "flex items-center gap-3 border-t px-4 py-2.5 text-sm sm:px-5",
                  off && "text-muted-foreground",
                )}
              >
                <span className="flex-1 font-medium">{section.label}</span>
                <span className="text-xs text-muted-foreground">
                  {off ? "Hidden while off" : section.required ? "Always shown" : section.variant}
                </span>
              </li>
            );
          })}
        </ul>
      </section>
    </>
  );
}

/** Small outlines of the header, footer and chat placement choices, by `field:value`. */
const FRAME_SKETCHES: Partial<Record<string, string>> = {
  "header:attached": "mt-0 h-2.5 w-full rounded-none",
  "header:floating": "mx-auto mt-1 h-2 w-[85%] rounded-full",
  "chrome:solid": "mt-0 h-2.5 w-full rounded-none",
  "chrome:glass": "mt-0 h-2.5 w-full rounded-none opacity-40 ring-1 ring-current",
  "chat:drawer": "my-0.5 ml-auto h-full w-1/3 rounded-sm",
  "chat:dock-left": "mr-auto h-full w-1/4 rounded-none",
  "chat:dock-right": "ml-auto h-full w-1/4 rounded-none",
  "footer:plain": "mt-auto h-3 w-full rounded-none",
  "footer:tear":
    "mt-auto h-3.5 w-full rounded-none [clip-path:polygon(0_35%,8%_0,16%_35%,24%_0,32%_35%,40%_0,48%_35%,56%_0,64%_35%,72%_0,80%_35%,88%_0,96%_35%,100%_10%,100%_100%,0_100%)]",
};

/** One site-frame field: as outlines where it has them, else as a segmented control. */
function FrameChoice({
  frameKey,
  theme,
  draft,
  canEdit,
  onChange,
}: {
  frameKey: FrameKey;
  theme: StorefrontTheme;
  draft: FrameDraft;
  canEdit: boolean;
  onChange: (draft: FrameDraft) => void;
}) {
  const choice: ThemeChoice = SITE_FRAME_CHOICES[frameKey];
  const value = draft[frameKey] ?? choiceValue(theme, choice);
  const set = (next: string | number) => onChange({ ...draft, [frameKey]: next });
  const sketched = choice.options.every(
    (option) => FRAME_SKETCHES[`${choice.field}:${option.value}`],
  );
  if (frameKey === "pageMargin") {
    return (
      <PageMarginField
        label={choice.label}
        presets={choice.options}
        value={Number(value)}
        onChange={set}
        disabled={!canEdit}
      />
    );
  }
  if (!sketched) {
    return (
      <ChoiceField
        id={`frame-${frameKey}`}
        label={choice.label}
        options={choice.options}
        value={value}
        onChange={set}
        disabled={!canEdit}
      />
    );
  }
  return (
    <fieldset className="grid gap-2" disabled={!canEdit}>
      <legend className="mb-2 text-sm font-medium">{choice.label}</legend>
      <div className="flex flex-wrap gap-2">
        {choice.options.map((option) => (
          <label
            key={String(option.value)}
            className={cn(
              "flex w-28 cursor-pointer flex-col items-center gap-1.5 rounded-lg border bg-card p-1.5 text-xs transition-colors has-focus-visible:ring-[3px] has-focus-visible:ring-ring/50 has-disabled:cursor-default",
              value === option.value
                ? "border-primary text-primary"
                : "border-border text-muted-foreground hover:border-primary/50",
            )}
          >
            <input
              type="radio"
              name={`frame-${frameKey}`}
              checked={value === option.value}
              onChange={() => set(option.value)}
              className="sr-only"
            />
            <span aria-hidden className="flex h-10 w-full flex-col rounded bg-muted p-1">
              <span
                className={cn(
                  "block bg-current opacity-50",
                  FRAME_SKETCHES[`${choice.field}:${option.value}`],
                )}
              />
            </span>
            <span className="font-medium text-foreground">{option.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * The page's side margin from lg: its presets, and a slider (with the px beside it) for any whole
 * px between `PAGE_MARGIN_PX`'s bounds. A value between presets selects none.
 */
function PageMarginField({
  label,
  presets,
  value,
  onChange,
  disabled,
}: {
  label: string;
  presets: ThemeChoice["options"];
  value: number;
  onChange: (value: number) => void;
  disabled: boolean;
}) {
  const set = (px: number) =>
    onChange(Math.min(PAGE_MARGIN_PX.max, Math.max(PAGE_MARGIN_PX.min, Math.round(px))));
  // The number field's text while it is being typed; null shows the value.
  const [typed, setTyped] = useState<string | null>(null);
  const commitTyped = () => {
    if (typed !== null && typed.trim() !== "" && Number.isFinite(Number(typed))) set(Number(typed));
    setTyped(null);
  };
  return (
    <div className="grid gap-2">
      <ChoiceField
        id="frame-pageMargin"
        label={label}
        options={presets}
        value={value}
        onChange={(px) => set(Number(px))}
        disabled={disabled}
      />
      <div className="flex items-center gap-2">
        <input
          type="range"
          aria-label={`${label} slider`}
          min={PAGE_MARGIN_PX.min}
          max={PAGE_MARGIN_PX.max}
          step={1}
          value={value}
          disabled={disabled}
          onChange={(e) => set(Number(e.target.value))}
          className="h-9 min-w-0 flex-1 cursor-pointer accent-primary disabled:cursor-default"
        />
        <Input
          type="number"
          aria-label={`${label} in px`}
          min={PAGE_MARGIN_PX.min}
          max={PAGE_MARGIN_PX.max}
          // What is typed stays as typed ("1" on the way to "100"), and is held to the bounds once
          // the field is left or Enter is pressed.
          value={typed ?? String(value)}
          disabled={disabled}
          onChange={(e) => setTyped(e.target.value)}
          onBlur={commitTyped}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitTyped();
          }}
          className="w-20 shrink-0"
        />
        <span className="text-sm text-muted-foreground">px</span>
      </div>
      <p className="text-xs text-muted-foreground">
        Room between the screen's edge and the page from desktop widths. Phones keep 16px and
        tablets 32px.
      </p>
    </div>
  );
}

/** The theme with the site frame's draft fields laid over the stored ones. */
function withFrame(theme: StorefrontTheme, draft: FrameDraft): StorefrontTheme {
  let next = theme;
  for (const [key, value] of Object.entries(draft) as [FrameKey, string | number][]) {
    next = withChoice(next, SITE_FRAME_CHOICES[key], value);
  }
  return next;
}

/** Whether the list entry is a page with layouts to choose from. */
function isPickable(id: StorefrontPagesEntryId): id is PickableStorefrontPage {
  return (PICKABLE_PAGES as readonly string[]).includes(id);
}

/** `["A", "B", "C"]` → `"A, B and C"`. */
function listWords(words: readonly string[]): string {
  if (words.length <= 1) return words.join("");
  return `${words.slice(0, -1).join(", ")} and ${words.at(-1)}`;
}

/** No saved picks: one object, so the preview's picks keep their identity until one is saved. */
const NO_PICKS: PageTemplateChoices = {};
